// Single source of truth for the Project Reporting UI Layout builder.
//
// All widgets live in ONE declarative registry (WIDGET_REGISTRY). Each widget
// declares which product types it can appear in (`products`) and, for
// Certification, which report types (`certTypes`). This is the single place
// that controls "which widget shows in which product type".
//
// IMPORTANT — two layers must agree for a widget to actually work in a product:
//   1. Here: add the product to the widget's `products` list (builder option).
//   2. Report hub: that product's report view must map widgetKey -> renderer
//      AND its data builder must supply the data the widget needs.
//        - HardwareReportView.js   -> LIVE_WIDGETS + dashboard.js
//        - SoftwareReportView.js    -> LIVE        + softwareDashboard.js
//        - CertificationReportView.js -> WIDGETS    + certification.js / certImages.js
// Hardware & Software share the same issue-data source, so issue widgets
// (highlight / state summary / severity) are reusable across both. Widgets that
// need scope-specific data (e.g. certification logo overview, CS/CG tracking)
// only have data under Certification.

export const CATEGORY = 'Project Reporting UI Layout';

export const PRODUCT_TYPES = ['Hardware', 'Software', 'Certification'];

// Certification report types == templateType values the hub renders per type.
export const CERT_REPORT_TYPES = [
  { key: 'bcst_daily', label: 'BCST Certification Daily Report' },
  { key: 'nbcst_daily', label: 'NBCST Certification Daily Report' },
  { key: 'weekly_highlight', label: 'Weekly Highlight' },
  { key: 'cs_cg_tracking', label: 'CS and CG Tracking' },
];

// Widget categories used by the section rules + Add-Widget modal tabs.
export const WIDGET_CATEGORIES = {
  chart: 'Chart',
  table_matrix: 'Table and Matrix',
  highlight: 'Highlight',
};

const ALL_CERT_DAILY = ['bcst_daily', 'nbcst_daily'];

// ── Global widget registry ───────────────────────────────────────────────────
// keyed by widgetKey (MUST match the report-hub renderer keys).
export const WIDGET_REGISTRY = {
  // Shared issue widgets (Hardware + Software).
  highlight_issues: {
    widgetName: 'Highlight Issues', category: 'highlight', products: ['Hardware', 'Software'],
    guide: { allowedSection: 'Highlight', multiSelect: '1 per section', dataSource: 'UrTracker DB (project issues)', output: 'Table', usage: 'Lists Blocker/Critical open issues for the project.', notes: 'Mandatory; cannot be removed.' },
  },
  state_summary: {
    widgetName: 'State Summary', category: 'table_matrix', products: ['Hardware', 'Software'],
    guide: { allowedSection: 'Table', multiSelect: 'Up to 2 per section', dataSource: 'UrTracker DB (project issues)', output: 'Table', usage: 'Issue counts grouped by workflow state and severity.', notes: '' },
  },
  severity_pie_chart: {
    widgetName: 'Severity Pie Chart', category: 'chart', products: ['Hardware', 'Software'],
    guide: { allowedSection: 'Chart', multiSelect: 'Up to 2 per section', dataSource: 'UrTracker DB (project issues)', output: 'Chart', usage: 'Open issues split by severity.', notes: 'Read-only, follows project/date filters.' },
  },

  // Hardware-only.
  issue_domain_chart: {
    widgetName: 'Issue Domain Chart', category: 'chart', products: ['Hardware'],
    guide: { allowedSection: 'Chart', multiSelect: 'Up to 2 per section', dataSource: 'UrTracker DB (project issues)', output: 'Chart', usage: 'Distribution of issues by problem domain.', notes: 'Read-only, follows project/date filters.' },
  },

  // Software-only.
  function_domain_chart: {
    widgetName: 'Function Domain Chart', category: 'chart', products: ['Software'],
    guide: { allowedSection: 'Chart', multiSelect: 'Up to 2 per section', dataSource: 'UrTracker DB (project issues)', output: 'Chart', usage: 'Distribution of issues by function/module.', notes: '' },
  },
  stability_table: {
    widgetName: 'Stability Table', category: 'table_matrix', products: ['Software'],
    guide: { allowedSection: 'Table', multiSelect: 'Up to 2 per section', dataSource: 'UrTracker DB (project issues)', output: 'Table', usage: 'Reboot / Crash / Hang stability buckets.', notes: '' },
  },

  // Certification widgets (scope-specific data — only available under Certification).
  cert_highlight: {
    widgetName: 'Highlight Issues', category: 'highlight', products: ['Certification'], certTypes: ALL_CERT_DAILY,
    guide: { allowedSection: 'Highlight', multiSelect: '1 per section', dataSource: 'UrTracker DB (cert model)', output: 'Table', usage: 'Daily highlight issues for the certification model.', notes: 'Mandatory for daily reports.' },
  },
  cert_weekly_highlight: {
    widgetName: 'Weekly Highlight', category: 'highlight', products: ['Certification'], certTypes: ['weekly_highlight'],
    guide: { allowedSection: 'Highlight', multiSelect: '1 per section', dataSource: 'UrTracker DB (cert model)', output: 'Table', usage: 'Weekly highlight summary per certification.', notes: 'Mandatory for the Weekly Highlight report.' },
  },
  cert_state_table: {
    widgetName: 'State Breakdown Table', category: 'table_matrix', products: ['Certification'], certTypes: ALL_CERT_DAILY,
    guide: { allowedSection: 'Table', multiSelect: 'Up to 2 per section', dataSource: 'UrTracker DB (cert model)', output: 'Table', usage: 'Issue states broken down by certification scope.', notes: '' },
  },
  cert_state_severity: {
    widgetName: 'State × Severity', category: 'table_matrix', products: ['Certification'], certTypes: ALL_CERT_DAILY,
    guide: { allowedSection: 'Table / Statistics', multiSelect: 'Up to 2 per section', dataSource: 'UrTracker DB (cert model)', output: 'Matrix', usage: 'State by severity matrix.', notes: '' },
  },
  cert_owner_matrix: {
    widgetName: 'Owner × State Matrix', category: 'table_matrix', products: ['Certification'], certTypes: ALL_CERT_DAILY,
    guide: { allowedSection: 'Table / Statistics', multiSelect: 'Up to 2 per section', dataSource: 'UrTracker DB (cert model)', output: 'Matrix', usage: 'Owner by state matrix.', notes: '' },
  },
  cert_trend_chart: {
    widgetName: 'Trend Chart', category: 'chart', products: ['Certification'], certTypes: ALL_CERT_DAILY,
    guide: { allowedSection: 'Chart', multiSelect: 'Up to 2 per section', dataSource: 'UrTracker DB (cert model)', output: 'Chart (image)', usage: 'Open PR trend over time.', notes: 'Generated image, refreshed on demand.' },
  },
  cert_throughput_chart: {
    widgetName: 'Throughput Chart', category: 'chart', products: ['Certification'], certTypes: ALL_CERT_DAILY,
    guide: { allowedSection: 'Chart', multiSelect: 'Up to 2 per section', dataSource: 'UrTracker DB (cert model)', output: 'Chart (image)', usage: 'Daily throughput of solved PRs.', notes: 'Generated image, refreshed on demand.' },
  },
  cert_logo_overview: {
    widgetName: 'Logo Status Overview', category: 'table_matrix', products: ['Certification'], certTypes: ALL_CERT_DAILY,
    guide: { allowedSection: 'Mandatory', multiSelect: '1 per section', dataSource: 'UrTracker DB (cert model)', output: 'Wide table (image)', usage: 'Logo certification status overview.', notes: 'Mandatory; cannot be removed.' },
  },
  cs_cg_cg_delay: {
    widgetName: 'CG Delay', category: 'table_matrix', products: ['Certification'], certTypes: ['cs_cg_tracking'],
    guide: { allowedSection: 'CG delay', multiSelect: '1 per section', dataSource: 'UrTracker DB (cert model)', output: 'Table', usage: 'Certifications delayed against CG target.', notes: '' },
  },
  cs_cg_cs_delay: {
    widgetName: 'CS Delay', category: 'table_matrix', products: ['Certification'], certTypes: ['cs_cg_tracking'],
    guide: { allowedSection: 'CS delay', multiSelect: '1 per section', dataSource: 'UrTracker DB (cert model)', output: 'Table', usage: 'Certifications delayed against CS target.', notes: '' },
  },
  cs_cg_cs_this_week: {
    widgetName: 'CS This Week', category: 'table_matrix', products: ['Certification'], certTypes: ['cs_cg_tracking'],
    guide: { allowedSection: 'CS this week', multiSelect: '1 per section', dataSource: 'UrTracker DB (cert model)', output: 'Table', usage: 'CS items scheduled this week.', notes: '' },
  },
  cs_cg_summary: {
    widgetName: 'Summary', category: 'table_matrix', products: ['Certification'], certTypes: ['cs_cg_tracking'],
    guide: { allowedSection: 'Summary', multiSelect: '1 per section', dataSource: 'UrTracker DB (cert model)', output: 'Table', usage: 'Certification summary counts.', notes: 'Mandatory; cannot be removed.' },
  },
};

export function getWidgetDef(widgetKey) {
  return WIDGET_REGISTRY[widgetKey] || null;
}

export function widgetMeta(subCategory, widgetKey) {
  return WIDGET_REGISTRY[widgetKey] || null;
}

// ── Default section layouts (mirror the backend seeders) ─────────────────────
// Used only when no template exists yet in the DB for an identity. Once a
// template is saved from the builder, the DB copy is the source of truth.
function w(widgetKey, side = 'full', locked = false) {
  return { widgetKey, side, locked };
}

const HARDWARE_SECTIONS = [
  { sectionKey: 'header', sectionName: 'Header', editable: false, rules: { maxWidgets: 0, allowedCategories: [], allowDuplicates: false, mandatory: true }, widgets: [] },
  { sectionKey: 'highlight', sectionName: 'Highlight Issues', editable: true, rules: { maxWidgets: 1, allowedCategories: ['highlight'], allowDuplicates: false, mandatory: true }, widgets: [w('highlight_issues', 'full', true)] },
  { sectionKey: 'chart', sectionName: 'Chart', editable: true, rules: { maxWidgets: 2, allowedCategories: ['chart'], allowDuplicates: false, mandatory: false }, widgets: [w('issue_domain_chart', 'left'), w('severity_pie_chart', 'right')] },
  { sectionKey: 'table', sectionName: 'Table', editable: true, rules: { maxWidgets: 2, allowedCategories: ['table_matrix'], allowDuplicates: false, mandatory: false }, widgets: [w('state_summary', 'full')] },
];

const SOFTWARE_SECTIONS = [
  { sectionKey: 'header', sectionName: 'Header', editable: false, rules: { maxWidgets: 0, allowedCategories: [], allowDuplicates: false, mandatory: true }, widgets: [] },
  { sectionKey: 'highlight', sectionName: 'Highlight Issues', editable: true, rules: { maxWidgets: 1, allowedCategories: ['highlight'], allowDuplicates: false, mandatory: true }, widgets: [w('highlight_issues', 'full', true)] },
  { sectionKey: 'table', sectionName: 'Table', editable: true, rules: { maxWidgets: 2, allowedCategories: ['table_matrix'], allowDuplicates: false, mandatory: false }, widgets: [w('state_summary', 'left'), w('stability_table', 'right')] },
  { sectionKey: 'chart', sectionName: 'Chart', editable: true, rules: { maxWidgets: 2, allowedCategories: ['chart'], allowDuplicates: false, mandatory: false }, widgets: [w('function_domain_chart', 'left'), w('severity_pie_chart', 'right')] },
];

const CERT_SECTIONS = {
  bcst_daily: [
    { sectionKey: 'header', sectionName: 'Header', editable: false, rules: { maxWidgets: 0, allowedCategories: [], allowDuplicates: false, mandatory: true }, widgets: [] },
    { sectionKey: 'highlight', sectionName: 'Highlight Issues', editable: true, rules: { maxWidgets: 1, allowedCategories: ['highlight'], allowDuplicates: false, mandatory: true }, widgets: [w('cert_highlight', 'full', true)] },
    { sectionKey: 'table', sectionName: 'Table', editable: true, rules: { maxWidgets: 2, allowedCategories: ['table_matrix'], allowDuplicates: false, mandatory: false }, widgets: [w('cert_state_table', 'left'), w('cert_owner_matrix', 'right')] },
    { sectionKey: 'chart', sectionName: 'Chart', editable: true, rules: { maxWidgets: 2, allowedCategories: ['chart'], allowDuplicates: false, mandatory: false }, widgets: [w('cert_trend_chart', 'left'), w('cert_throughput_chart', 'right')] },
    { sectionKey: 'mandatory', sectionName: 'Logo Status Overview', editable: false, rules: { maxWidgets: 1, allowedCategories: ['table_matrix'], allowDuplicates: false, mandatory: true }, widgets: [w('cert_logo_overview', 'full', true)] },
  ],
  nbcst_daily: [
    { sectionKey: 'header', sectionName: 'Header', editable: false, rules: { maxWidgets: 0, allowedCategories: [], allowDuplicates: false, mandatory: true }, widgets: [] },
    { sectionKey: 'highlight', sectionName: 'Highlight Issues', editable: true, rules: { maxWidgets: 1, allowedCategories: ['highlight'], allowDuplicates: false, mandatory: true }, widgets: [w('cert_highlight', 'full', true)] },
    { sectionKey: 'statistics', sectionName: 'Statistics', editable: true, rules: { maxWidgets: 2, allowedCategories: ['table_matrix'], allowDuplicates: false, mandatory: false }, widgets: [w('cert_state_severity', 'left'), w('cert_owner_matrix', 'right')] },
    { sectionKey: 'mandatory', sectionName: 'Logo Status Overview', editable: false, rules: { maxWidgets: 1, allowedCategories: ['table_matrix'], allowDuplicates: false, mandatory: true }, widgets: [w('cert_logo_overview', 'full', true)] },
  ],
  weekly_highlight: [
    { sectionKey: 'header', sectionName: 'Header', editable: false, rules: { maxWidgets: 0, allowedCategories: [], allowDuplicates: false, mandatory: true }, widgets: [] },
    { sectionKey: 'highlight', sectionName: 'Highlight Issues', editable: true, rules: { maxWidgets: 1, allowedCategories: ['highlight'], allowDuplicates: false, mandatory: true }, widgets: [w('cert_weekly_highlight', 'full', true)] },
  ],
  cs_cg_tracking: [
    { sectionKey: 'header', sectionName: 'Header', editable: false, rules: { maxWidgets: 0, allowedCategories: [], allowDuplicates: false, mandatory: true }, widgets: [] },
    { sectionKey: 'cg_delay', sectionName: 'CG delay', editable: true, rules: { maxWidgets: 1, allowedCategories: ['table_matrix'], allowDuplicates: false, mandatory: false }, widgets: [w('cs_cg_cg_delay', 'full')] },
    { sectionKey: 'cs_delay', sectionName: 'CS delay', editable: true, rules: { maxWidgets: 1, allowedCategories: ['table_matrix'], allowDuplicates: false, mandatory: false }, widgets: [w('cs_cg_cs_delay', 'full')] },
    { sectionKey: 'cs_this_week', sectionName: 'CS this week', editable: true, rules: { maxWidgets: 1, allowedCategories: ['table_matrix'], allowDuplicates: false, mandatory: false }, widgets: [w('cs_cg_cs_this_week', 'full')] },
    { sectionKey: 'summary', sectionName: 'Summary', editable: false, rules: { maxWidgets: 1, allowedCategories: ['table_matrix'], allowDuplicates: false, mandatory: true }, widgets: [w('cs_cg_summary', 'full', true)] },
  ],
};

const CERT_LABELS = CERT_REPORT_TYPES.reduce((m, t) => ({ ...m, [t.key]: t.label }), {});

// Resolve the template identity + default name for a product type / report type.
export function resolveIdentity(subCategory, templateType) {
  if (subCategory === 'Certification') {
    const tt = templateType || 'bcst_daily';
    return { category: CATEGORY, subCategory, templateType: tt, name: CERT_LABELS[tt] || tt, isDefault: tt === 'bcst_daily' };
  }
  return { category: CATEGORY, subCategory, templateType: 'default', name: 'Default template', isDefault: true };
}

// Build the default sections (expanded to full widget objects) for an identity.
export function buildDefaultSections(subCategory, templateType) {
  let raw;
  if (subCategory === 'Hardware') raw = HARDWARE_SECTIONS;
  else if (subCategory === 'Software') raw = SOFTWARE_SECTIONS;
  else raw = CERT_SECTIONS[templateType || 'bcst_daily'] || [];

  return raw.map((sec, sIdx) => ({
    sectionKey: sec.sectionKey,
    sectionName: sec.sectionName,
    position: sIdx,
    editable: sec.editable,
    rules: { ...sec.rules },
    widgets: sec.widgets.map((wd, wIdx) => {
      const def = WIDGET_REGISTRY[wd.widgetKey] || {};
      return {
        widgetKey: wd.widgetKey,
        widgetName: def.widgetName || wd.widgetKey,
        category: def.category || 'table_matrix',
        position: wIdx,
        side: wd.side,
        subIndex: 0,
        locked: !!wd.locked,
        config: {},
      };
    }),
  }));
}

// Available widgets of a category for the Add-Widget modal, scoped to the
// current product type (and, for Certification, the selected report type).
export function widgetsForCategory(subCategory, category, templateType) {
  return Object.entries(WIDGET_REGISTRY)
    .filter(([, def]) => def.category === category && (def.products || []).includes(subCategory))
    .filter(([, def]) => {
      if (subCategory !== 'Certification') return true;
      if (!templateType || !def.certTypes) return true;
      return def.certTypes.includes(templateType);
    })
    .map(([widgetKey, def]) => ({ widgetKey, widgetName: def.widgetName, category: def.category, guide: def.guide }));
}
